import { SurgicalPendencyRepository as Repository } from 'repository/interfaces/surgical-pendency-repository'
import { AddSurgicalPendency } from 'domain/usecases/surgical-pendency/add-surgical-pendency'
import { IApiRepository } from 'service/protocols/api/api-repository'
import {
  answerSurgicalPendency,
  createSurgicalPendency,
  finishSurgicalPendency
} from 'repository/graphql/mutations'
import { makeGraphQLVariable } from 'repository/graphql/utils/make-variables'
import { ApiStatusCode } from 'service/protocols/api/api-client'
import handleGraphQLError from 'repository/graphql/utils/handle-error'
import { RepositoryErrors } from 'repository/errors/repository-errors'
import { AnswerSurgicalPendency } from 'domain/usecases/surgical-pendency/answer-surgical-pendency'
import { UploadSurgicalPendencyDocument } from 'domain/usecases/surgical-pendency/upload-surgical-pendency-document'
import { LoadSurgicalPendencyDocument } from 'domain/usecases/surgical-pendency/load-surgical-pendency-document'
import { FinishSurgicalPendency } from 'domain/usecases/surgical-pendency/finish-surgical-pendency'
import { LoadSurgicalPendency } from 'domain/usecases/surgical-pendency/load-surgical-pendency'
import { LoadSurgicalOrderTimeLine } from 'domain/usecases/surgical-pendency/load-surgical-order-time-line'
import { LoadSurgicalPendencyHistory } from 'domain/usecases/surgical-pendency/load-surgical-pendency-history'
import { LoadSurgicalOrderNoticeCard } from 'domain/usecases/surgical-pendency/load-surgical-order-notice-card'
import { CustomError } from 'common/errors'
import { LoadHelpPendencyHistory } from 'domain/usecases/surgical-pendency/load-help-pendency-history'
import { CreateHelpPendencyMessage } from 'domain/usecases/surgical-pendency/create-help-pendency-message'
import { FinishHelpPendency } from 'domain/usecases/surgical-pendency/finish-help-pendency'
import { LoadAuthorizedCommunicationHistory } from 'domain/usecases/surgical-pendency/load-authorized-communication-history'
import { CreateAuthorizedCommunicationMessage } from 'domain/usecases/surgical-pendency/create-authorized-communication-message'
import { FinishAuthorizedCommunication } from 'domain/usecases/surgical-pendency/finish-authorized-communication'
import { AnswerDoctorCommunication } from 'domain/usecases/surgical-pendency/answer-doctor-communication'

export class SurgicalPendencyRepository implements Repository {
  constructor(private readonly apiRepository: IApiRepository) {}

  async addSurgicalPendency(
    params: AddSurgicalPendency.Params
  ): Promise<AddSurgicalPendency.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<AddSurgicalPendency.Model>

    const query = createSurgicalPendency

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params, 'data')
      },
      query: query.name
    })

    if (httpResponse.statusCode !== ApiStatusCode.ok) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as AddSurgicalPendency.Model
    }
  }

  async answerSurgicalPendency({
    group_id,
    ...data
  }: AnswerSurgicalPendency.Params): Promise<AnswerSurgicalPendency.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<AnswerSurgicalPendency.Model>

    const query = answerSurgicalPendency
    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(data, 'data'),
          ...makeGraphQLVariable(group_id, 'group_id')
        }
      },
      query: query.name
    })

    if (httpResponse.statusCode !== ApiStatusCode.ok) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as AnswerSurgicalPendency.Model
    }
  }

  async uploadSurgicalPendencyDocument(
    params: UploadSurgicalPendencyDocument.Params
  ): Promise<UploadSurgicalPendencyDocument.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UploadSurgicalPendencyDocument.Model>

    const httpResponse = await apiRepository.post({
      url: '/surgical-pendency/upload-attachment',
      body: params.form
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as UploadSurgicalPendencyDocument.Model
    }
  }

  async loadSurgicalPendencyDocument(
    surgical_order_id: number,
    document_id: number,
    group_id?: string
  ): Promise<LoadSurgicalPendencyDocument.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalPendencyDocument.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-pendency/${surgical_order_id}/attachment/${document_id}${
          group_id ? `?group_id=${group_id}` : ''
        }`
      },
      true
    )

    if (httpResponse.status && httpResponse.status !== ApiStatusCode.ok) {
      throw new CustomError('Ocorreu um erro ao buscar o documento')
    } else {
      const contentType = httpResponse.headers['content-type']
      const data = httpResponse.data

      return {
        contentType,
        data
      }
    }
  }

  async finishSurgicalPendency(
    params: FinishSurgicalPendency.Params
  ): Promise<FinishSurgicalPendency.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<FinishSurgicalPendency.Model>

    const query = finishSurgicalPendency

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params, 'data')
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as FinishSurgicalPendency.Model
    }
  }

  async loadSurgicalPendency(
    surgical_order_id: number
  ): Promise<LoadSurgicalPendency.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalPendency.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-pendency/surgical-order/${surgical_order_id}`
      },
      false
    )

    return {
      data: httpResponse.body
    }
  }

  async loadSurgicalOrderTimeLine(
    surgical_order_id: number
  ): Promise<LoadSurgicalOrderTimeLine.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrderTimeLine.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-order/time-line?surgical_order_id=${surgical_order_id}`
      },
      false
    )

    return {
      data: httpResponse.body
    }
  }

  async loadSurgicalPendencyHistory(
    surgical_pendency_id: number
  ): Promise<LoadSurgicalPendencyHistory.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalPendencyHistory.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-pendency/historic/${surgical_pendency_id}`
      },
      false
    )

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError(
        'Falha ao buscar dados da linha do tempo da pendência'
      )
    } else {
      return httpResponse.body as LoadSurgicalPendencyHistory.Model
    }
  }

  async loadHelpPendencyHistory(
    surgical_pendency_id: LoadHelpPendencyHistory.Params
  ): Promise<LoadHelpPendencyHistory.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadHelpPendencyHistory.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/active-searchers/${surgical_pendency_id}/historic`
    })

    return httpResponse.body as LoadHelpPendencyHistory.Model
  }

  async createHelpPendencyMessage(
    params: CreateHelpPendencyMessage.Params
  ): Promise<CreateHelpPendencyMessage.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateHelpPendencyMessage.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/active-searchers/${params.surgical_pendency_id}/historic`,
      body: {
        message: params.message
      }
    })

    return httpResponse.body as CreateHelpPendencyMessage.Model
  }

  async finishHelpPendency(
    params: FinishHelpPendency.Params
  ): Promise<FinishHelpPendency.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateHelpPendencyMessage.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/active-searchers/${params.surgical_pendency_id}/finish`,
      body: {
        message: params.message
      }
    })

    return httpResponse.body as CreateHelpPendencyMessage.Model
  }

  async loadSurgicalOrderNoticeCard(
    surgical_order_id: number
  ): Promise<LoadSurgicalOrderNoticeCard.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrderNoticeCard.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-order/notice-card?surgical_order_id=${surgical_order_id}`
      },
      false
    )

    return {
      data: httpResponse.body
    }
  }

  async loadAuthorizedCommunicationHistory(
    surgical_pendency_id: LoadAuthorizedCommunicationHistory.Params
  ): Promise<LoadAuthorizedCommunicationHistory.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadAuthorizedCommunicationHistory.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/authorized-communications/${surgical_pendency_id}`
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new CustomError(
        'Ocorreu um erro ao buscar a Comunicação autorizada'
      )
    } else {
      return httpResponse.body as LoadAuthorizedCommunicationHistory.Model
    }
  }

  async createAuthorizedCommunicationMessage(
    params: CreateAuthorizedCommunicationMessage.Params
  ): Promise<CreateAuthorizedCommunicationMessage.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateAuthorizedCommunicationMessage.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/authorized-communications/${params.surgical_pendency_id}/historic`,
      body: {
        message: params.message
      }
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw new CustomError('Ocorreu um erro ao adicionar comentário')
    } else {
      return httpResponse.body as CreateAuthorizedCommunicationMessage.Model
    }
  }

  async finishAuthorizedCommunication(
    params: FinishAuthorizedCommunication.Params
  ): Promise<FinishAuthorizedCommunication.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<FinishAuthorizedCommunication.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/authorized-communications/${params.surgical_pendency_id}/finish`
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.noContent
    ) {
      throw new CustomError('Ocorreu um erro ao marcar como resolvida')
    } else {
      return httpResponse.body as FinishAuthorizedCommunication.Model
    }
  }

  async answerDoctorCommunication(
    params: AnswerDoctorCommunication.Params
  ): Promise<AnswerDoctorCommunication.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<AnswerDoctorCommunication.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/surgical-pendency/doctor-communication/${params.surgical_pendency_id}/answer`,
      body: {
        message: params.message
      }
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw new CustomError('Ocorreu um erro ao responder a conversa')
    } else {
      return httpResponse.body as AnswerDoctorCommunication.Model
    }
  }
}
